import React, { useEffect, useState } from 'react';
import { Post } from '../../models/post';
import { ChildProfile } from '../../models/childProfile';
import BuildReportPanel from './BuildReportPanel';
import PeriodNavigator from './PeriodNavigator';
import ReportHistory from './ReportHistory';
import TopTabs, { TopTab } from '../common/TopTabs';
import { useUrlTab } from '../../hooks/useUrlTab';
import { ReportTemplate, STOCK_WEEKLY_TEMPLATE } from '../../models/reportTemplate';
import { ReportTemplateDataService } from '../../services/reportTemplateDataService';
import { getMonday, getFriday, toDateString } from '../../services/dateUtils';
import rustleLogo from '../../images/rustle_it_logo.png';
import './reportsTabs.scss';

type TabKey = 'build' | 'history';

interface ReportsTabsProps {
  // Build Report tab inputs — every post in the period, drafts included.
  posts: Post[];
  filterChild: string | null;
  filterFromDate: string | null;
  filterToDate: string | null;
  childProfiles: ChildProfile[];
  onPeriodChange: (startDate: string | null, endDate: string | null) => void;
  onReloadPosts: (skipConfirm?: boolean) => Promise<void>;
  // Shared
  notice: string | null;
  onNotice: (msg: string | null) => void;
  /** Notifies the parent which tab is active — Report History treats the child
   *  as an optional filter, so it never demands a selection. */
  onTabChange?: (tab: TabKey) => void;
  /** Full-area child picker, supplied while the Build Report tab is waiting on
   *  a selection. Renders in place of the panel body so the tabs stay usable. */
  childPicker?: React.ReactNode;
}

const TABS: TopTab<TabKey>[] = [
  { id: 'build',   label: 'Build Report',   icon: 'fa-file-pen' },
  { id: 'history', label: 'Report History', icon: 'fa-folder-tree' },
];

/** Dropdown key: DB row id, or 'stock' for the code-defined default. */
const templateKey = (t: ReportTemplate): string => (t.id !== null ? String(t.id) : 'stock');

const ReportsTabs: React.FC<ReportsTabsProps> = (props) => {
  const {
    posts,
    filterChild,
    filterFromDate,
    filterToDate,
    childProfiles,
    onPeriodChange,
    onReloadPosts,
    notice,
    onNotice,
    onTabChange,
    childPicker,
  } = props;

  // In the URL, so /reports?tab=history is a link someone can send.
  const [tab, setTab] = useUrlTab<TabKey>('tab', ['build', 'history'], 'build');

  useEffect(() => {
    onTabChange?.(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ── Template selection — feeds the Build Report tab ──────────────────────
  // The dropdown dictates the timeframe, which dictates the period control,
  // which dictates the date range of posts reviewed/published and reported.
  const [templates, setTemplates] = useState<ReportTemplate[]>([STOCK_WEEKLY_TEMPLATE]);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate>(STOCK_WEEKLY_TEMPLATE);
  const [reloadSpinning, setReloadSpinning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ReportTemplateDataService.listTemplates()
      .then((list) => {
        if (cancelled || list.length === 0) return;
        setTemplates(list);
        // First entry is the stock Weekly Report (or the org's override of
        // it) — the guaranteed-valid default selection. Same timeframe as
        // the initial state, so the period Home.tsx already set stands.
        setSelectedTemplate(list[0]);
      })
      .catch((err) => {
        // Dropdown falls back to the code-defined stock template only.
        console.error('Failed to load report templates:', err);
      });
    return () => { cancelled = true; };
  }, []);

  const handleTemplateSelect = (key: string) => {
    const next = templates.find((t) => templateKey(t) === key);
    if (!next) return;
    const prevTimeframe = selectedTemplate.config.timeframe;
    setSelectedTemplate(next);
    // A new timeframe needs a matching period — jump to the current one.
    // (custom keeps whatever range is set: it's a free range by definition.)
    if (next.config.timeframe !== prevTimeframe) {
      const now = new Date();
      switch (next.config.timeframe) {
        case 'daily': {
          const today = toDateString(now);
          onPeriodChange(today, today);
          break;
        }
        case 'weekly':
          onPeriodChange(toDateString(getMonday(now)), toDateString(getFriday(now)));
          break;
        case 'monthly':
          onPeriodChange(
            toDateString(new Date(now.getFullYear(), now.getMonth(), 1)),
            toDateString(new Date(now.getFullYear(), now.getMonth() + 1, 0))
          );
          break;
        case 'custom':
          break;
      }
    }
  };

  const handleReload = async () => {
    setReloadSpinning(true);
    try {
      await onReloadPosts();
    } finally {
      setReloadSpinning(false);
    }
  };

  // Template + period sit together on the left: between them they answer
  // "which report am I building", so they read as one statement rather than
  // two controls at opposite ends of the bar. Reload stays right — it's a
  // utility, not part of that statement.
  const periodToolbar = (
    <div className="pill-toolbar pill-toolbar--week" role="toolbar" aria-label="Report template and period">
      <div className="pill-toolbar__side pill-toolbar__side--left">
        {/* Labelled: an unlabelled pill this close to the tab strip reads as a
            filter on the posts rather than the report's template. */}
        <span className="reports-template-label">Template</span>
        <select
          className="reports-template-select"
          data-help="reports.template"
          value={templateKey(selectedTemplate)}
          onChange={(e) => handleTemplateSelect(e.target.value)}
          aria-label="Report template"
          title="Report template"
        >
          {templates.map((t) => (
            <option key={templateKey(t)} value={templateKey(t)}>{t.name}</option>
          ))}
        </select>
        <PeriodNavigator
          timeframe={selectedTemplate.config.timeframe}
          startDate={filterFromDate}
          endDate={filterToDate}
          onPeriodChange={onPeriodChange}
        />
      </div>
      <div className="pill-toolbar__side pill-toolbar__side--right">
        <button
          className="pill-toolbar__icon-btn"
          onClick={handleReload}
          disabled={reloadSpinning}
          title="Reload posts"
          aria-label="Reload posts"
        >
          <i className="fas fa-sync-alt" style={reloadSpinning ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined} />
        </button>
      </div>
    </div>
  );

  const selectedChild = filterChild
    ? childProfiles.find(p => String(p.id) === filterChild) ?? null
    : null;
  const selectedChildName = selectedChild
    ? `${selectedChild.first_name} ${selectedChild.last_name}`
    : '';

  const handleSetNotice = (msg: string) => onNotice(msg);

  const pickChildPlaceholder = (
    <div className="d-flex flex-column align-items-center justify-content-center w-100 p-5 text-center" style={{ flex: 1 }}>
      <img src={rustleLogo} alt="Rustle logo" className="home-logo-rise" style={{ maxWidth: '240px', marginBottom: '3rem' }} />
      <p
        className="text-muted fst-italic mb-0"
        style={{ fontSize: '17px', letterSpacing: '0.01em' }}
      >
        Pick a child from the sidebar to view their reports.
      </p>
    </div>
  );

  return (
    <div className="reports-tabs top-tabs-page">
      <TopTabs<TabKey>
        tabs={TABS}
        active={tab}
        onChange={setTab}
        ariaLabel="Reports view"
        dataHelp="reports.tabs"
      />

      {notice && (
        <div className="alert alert-success mb-0 px-4 py-2 rounded-0" style={{ flexShrink: 0 }}>
          {notice}
        </div>
      )}

      {tab === 'build' && (
        <div className="reports-tabs__panel reports-tabs__panel--current">
          {childPicker ?? (!filterChild ? pickChildPlaceholder : (
            <>
              {periodToolbar}
              <BuildReportPanel
                posts={posts}
                childName={selectedChildName}
                child={selectedChild}
                childId={filterChild}
                startDate={filterFromDate}
                endDate={filterToDate}
                template={selectedTemplate}
                onReloadPosts={onReloadPosts}
                onNotice={handleSetNotice}
              />
            </>
          ))}
        </div>
      )}

      {tab === 'history' && (
        <div className="reports-tabs__panel reports-tabs__panel--history">
          <ReportHistory
            childProfiles={childProfiles}
            sidebarChildId={filterChild}
            onRegenerateSuccess={handleSetNotice}
          />
        </div>
      )}
    </div>
  );
};

export default ReportsTabs;
